/**
 * Supabase vu du site d'administration : un client ordinaire (ADR-0005).
 *
 * La clé publique et la session de l'administrateur, rien d'autre : ce que le
 * site peut lire ou écrire, c'est la base qui le décide, fonction par fonction,
 * en vérifiant la table `administrateurs`. Une clé secrète ici serait lisible
 * par quiconque ouvre la page.
 *
 * Connexion par lien magique : Supabase envoie un lien qui revient sur ce site
 * avec les jetons dans l'ancre de l'adresse (`#access_token=…`).
 */

const URL_BASE = import.meta.env.VITE_SUPABASE_URL ?? "";
const CLE = import.meta.env.VITE_SUPABASE_ANON_KEY ?? "";
const STOCKAGE = "lexireveal-admin-session";

/** Marge avant expiration : un jeton qui expire pendant l'appel ferait échouer l'enregistrement. */
const MARGE_MS = 60_000;

export type Session = {
  readonly accessToken: string;
  readonly refreshToken: string;
  /** Expiration du jeton d'accès, en millisecondes depuis 1970. */
  readonly expiresAt: number;
  readonly email: string;
};

/** Erreur métier : `code` est le message levé par la fonction SQL (`acces_refuse`, `motif_requis`…). */
export class ErreurBase extends Error {
  constructor(readonly code: string) {
    super(code);
    this.name = "ErreurBase";
  }
}

export function estConfigure(): boolean {
  return URL_BASE.length > 0 && CLE.length > 0;
}

function lireStockage(): Session | null {
  try {
    const texte = window.localStorage.getItem(STOCKAGE);
    if (texte === null) return null;
    const data = JSON.parse(texte) as Partial<Session>;
    return typeof data.accessToken === "string" &&
      typeof data.refreshToken === "string" &&
      typeof data.expiresAt === "number" &&
      typeof data.email === "string"
      ? (data as Session)
      : null;
  } catch {
    return null;
  }
}

function garder(session: Session | null): Session | null {
  try {
    if (session === null) window.localStorage.removeItem(STOCKAGE);
    else window.localStorage.setItem(STOCKAGE, JSON.stringify(session));
  } catch {
    // Stockage refusé (navigation privée) : la session vit le temps de l'onglet.
  }
  session_ = session;
  return session;
}

let session_: Session | null = null;

async function auth(chemin: string, init: RequestInit & { jeton?: string } = {}): Promise<Response> {
  const { jeton, ...reste } = init;
  try {
    return await fetch(`${URL_BASE}/auth/v1/${chemin}`, {
      ...reste,
      headers: {
        "Content-Type": "application/json",
        apikey: CLE,
        ...(jeton === undefined ? {} : { Authorization: `Bearer ${jeton}` }),
      },
    });
  } catch {
    throw new ErreurBase("hors_ligne");
  }
}

/**
 * Demande un lien de connexion. `create_user: false` : le site n'inscrit
 * personne — seul un compte créé à la main dans Supabase reçoit un lien, ce
 * qui empêche aussi de se servir de ce formulaire pour écrire à n'importe qui.
 */
export async function envoyerLien(email: string, retour: string): Promise<void> {
  const reponse = await auth(`otp?redirect_to=${encodeURIComponent(retour)}`, {
    method: "POST",
    body: JSON.stringify({ email: email.trim(), create_user: false }),
  });
  if (reponse.status === 429) throw new ErreurBase("trop_de_demandes");
  // Adresse inconnue : Supabase refuse (« Signups not allowed for otp »). Le
  // site ne le dit pas, pour ne pas révéler quelles adresses ont un compte.
  if (!reponse.ok && reponse.status !== 400 && reponse.status !== 422) throw new ErreurBase("envoi_impossible");
}

type ReponseJetons = {
  access_token?: string | undefined;
  refresh_token?: string | undefined;
  expires_in?: number | undefined;
  user?: { email?: string | undefined } | undefined;
};

async function versSession(data: ReponseJetons): Promise<Session | null> {
  if (
    typeof data.access_token !== "string" ||
    typeof data.refresh_token !== "string" ||
    typeof data.expires_in !== "number"
  ) {
    return null;
  }
  let email = data.user?.email;
  if (email === undefined) {
    const reponse = await auth("user", { method: "GET", jeton: data.access_token });
    email = reponse.ok ? ((await reponse.json()) as { email?: string }).email : undefined;
  }
  return {
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    expiresAt: Date.now() + data.expires_in * 1000,
    email: email ?? "",
  };
}

/**
 * Au chargement : un retour de lien magique, sinon la session gardée. L'ancre
 * est effacée de l'adresse aussitôt lue — elle contient les jetons.
 */
export function ouvrirSession(): Promise<{ session: Session | null; erreurLien: string | null }> {
  // Une seule lecture par chargement de page : l'ancre est effacée à la première.
  ouverture ??= lireOuverture();
  return ouverture;
}

let ouverture: Promise<{ session: Session | null; erreurLien: string | null }> | null = null;

async function lireOuverture(): Promise<{ session: Session | null; erreurLien: string | null }> {
  const ancre = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  if (ancre.has("access_token") || ancre.has("error")) {
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    if (ancre.has("error")) {
      return { session: garder(lireStockage()), erreurLien: ancre.get("error_description") ?? ancre.get("error") };
    }
    const session = await versSession({
      access_token: ancre.get("access_token") ?? undefined,
      refresh_token: ancre.get("refresh_token") ?? undefined,
      expires_in: Number(ancre.get("expires_in") ?? "3600"),
    } as ReponseJetons);
    return { session: garder(session), erreurLien: session === null ? "lien incomplet" : null };
  }
  return { session: garder(lireStockage()), erreurLien: null };
}

export function deconnecter(): void {
  const session = session_;
  garder(null);
  if (session !== null) {
    // Révoque aussi le jeton de rafraîchissement ; un échec n'empêche pas de partir.
    void auth("logout", { method: "POST", jeton: session.accessToken }).catch(() => undefined);
  }
}

/** Un jeton d'accès valable, rafraîchi s'il expire bientôt. */
export async function jetonValable(): Promise<string> {
  const session = session_;
  if (session === null) throw new ErreurBase("session_expiree");
  if (session.expiresAt - MARGE_MS > Date.now()) return session.accessToken;

  const reponse = await auth("token?grant_type=refresh_token", {
    method: "POST",
    body: JSON.stringify({ refresh_token: session.refreshToken }),
  });
  const nouvelle = reponse.ok ? await versSession((await reponse.json()) as ReponseJetons) : null;
  if (nouvelle === null) {
    garder(null);
    throw new ErreurBase("session_expiree");
  }
  garder({ ...nouvelle, email: nouvelle.email || session.email });
  return nouvelle.accessToken;
}

/** Appelle une fonction SQL par l'API REST (`/rest/v1/rpc/<fonction>`). */
export async function appeler<T>(fonction: string, args: Record<string, unknown> = {}): Promise<T> {
  const jeton = await jetonValable();
  let reponse: Response;
  try {
    reponse = await fetch(`${URL_BASE}/rest/v1/rpc/${fonction}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", apikey: CLE, Authorization: `Bearer ${jeton}` },
      body: JSON.stringify(args),
    });
  } catch {
    throw new ErreurBase("hors_ligne");
  }
  const data = (await reponse.json().catch(() => null)) as unknown;
  if (!reponse.ok) {
    if (reponse.status === 401) {
      garder(null);
      throw new ErreurBase("session_expiree");
    }
    const message = (data as { message?: unknown } | null)?.message;
    throw new ErreurBase(typeof message === "string" ? message : "erreur_inconnue");
  }
  return data as T;
}
